import { useState, useEffect } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  Award, Star, Gift, Users, RefreshCw, Plus, TrendingUp, Crown,
  ChevronRight, Settings2, X, Coins, Save, Trash2, Timer, DollarSign, Pencil
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

const tierColors = { Bronze: "#CD7F32", Silver: "#C0C0C0", Gold: "#F59E0B", Platinum: "#1A1A1A" };

export default function LoyaltyPage() {
  const [dashboard, setDashboard] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [tierFilter, setTierFilter] = useState("all");
  const [showEnroll, setShowEnroll] = useState(false);
  const [clients, setClients] = useState([]);
  const [enrollClient, setEnrollClient] = useState("");
  const [selectedMember, setSelectedMember] = useState(null);
  const [showRedeem, setShowRedeem] = useState(null);
  const [rulesForm, setRulesForm] = useState(null);
  const [savingRules, setSavingRules] = useState(false);
  const [editTiers, setEditTiers] = useState([]);
  const [editRewards, setEditRewards] = useState([]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [dashRes, memRes, cliRes] = await Promise.all([
        api.get("/loyalty/dashboard"),
        api.get("/loyalty/members"),
        api.get("/clients?limit=200"),
      ]);
      setDashboard(dashRes.data);
      setMembers(memRes.data || []);
      setClients((cliRes.data?.clients || cliRes.data || []));
    } catch { toast.error("Failed to load loyalty data"); }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const enroll = async () => {
    if (!enrollClient) return;
    try {
      await api.post("/loyalty/enroll", { client_id: enrollClient });
      toast.success("Client enrolled in loyalty program!");
      setShowEnroll(false);
      setEnrollClient("");
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to enroll"); }
  };

  const redeemReward = async (clientId, rewardId) => {
    try {
      const res = await api.post("/loyalty/redeem", { client_id: clientId, reward_id: rewardId });
      toast.success(`Redeemed: ${res.data.reward} (-${res.data.points_redeemed} pts)`);
      setShowRedeem(null);
      fetchAll();
      if (selectedMember) viewMember(clientId);
    } catch (e) { toast.error(e.response?.data?.detail || "Redemption failed"); }
  };

  const viewMember = async (clientId) => {
    try {
      const res = await api.get(`/loyalty/members/${clientId}`);
      setSelectedMember(res.data);
    } catch { toast.error("Failed to load member"); }
  };

  const openRules = () => {
    const c = dashboard?.config || {};
    setRulesForm({
      points_per_dollar: c.points_per_dollar || 10,
      points_dollar_value: c.points_dollar_value || 0.01,
      referral_bonus: c.referral_bonus || 500,
      review_bonus: c.review_bonus || 100,
      birthday_bonus: c.birthday_bonus || 250,
      points_expire: c.points_expire || false,
      expiry_days: c.expiry_days || 365,
      min_redeem_points: c.min_redeem_points || 500,
    });
    setEditTiers((c.tiers || []).map((t, i) => ({ ...t, _key: `t_${i}` })));
    setEditRewards((c.rewards || []).map((r, i) => ({ ...r, _key: `r_${i}` })));
    setTab("rules");
  };

  const saveRules = async () => {
    setSavingRules(true);
    try {
      const payload = {
        ...rulesForm,
        tiers: editTiers.map(({ _key, ...t }) => t),
        rewards: editRewards.map(({ _key, ...r }) => r),
      };
      await api.put("/loyalty/config", payload);
      toast.success("Loyalty rules saved");
      fetchAll();
    } catch { toast.error("Failed to save rules"); }
    setSavingRules(false);
  };

  const addTier = () => {
    setEditTiers(prev => [...prev, { name: "New Tier", min_points: 0, discount_pct: 0, color: "#6B7280", _key: `t_${Date.now()}` }]);
  };
  const updateTier = (idx, key, val) => setEditTiers(prev => prev.map((t, i) => i === idx ? { ...t, [key]: val } : t));
  const removeTier = (idx) => setEditTiers(prev => prev.filter((_, i) => i !== idx));

  const addReward = () => {
    setEditRewards(prev => [...prev, {
      reward_id: `rwd_${Date.now()}`, name: "New Reward", points_cost: 1000,
      type: "discount", value: 10, description: "", _key: `r_${Date.now()}`
    }]);
  };
  const updateReward = (idx, key, val) => setEditRewards(prev => prev.map((r, i) => i === idx ? { ...r, [key]: val } : r));
  const removeReward = (idx) => setEditRewards(prev => prev.filter((_, i) => i !== idx));

  const filteredMembers = tierFilter === "all" ? members : members.filter(m => m.tier === tierFilter);
  const config = dashboard?.config || {};
  const rewards = config.rewards || [];
  const tiers = (config.tiers || []).sort((a, b) => a.min_points - b.min_points);
  const enrolledIds = new Set(members.map(m => m.client_id));
  const unenrolled = clients.filter(c => !enrolledIds.has(c.client_id));

  if (loading) return <div className="p-8 flex items-center justify-center min-h-[400px]"><RefreshCw className="w-5 h-5 animate-spin text-rose-400" /></div>;

  return (
    <div data-testid="loyalty-page" className="p-6 lg:p-8 max-w-[1400px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-['Outfit'] text-2xl font-semibold text-slate-900 tracking-tight">Loyalty & Rewards</h1>
          <p className="text-sm text-slate-400 mt-1">Points program with tiered rewards to drive retention</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setShowEnroll(true)} className="border-slate-200" data-testid="enroll-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Enroll Client
          </Button>
          <Button size="sm" onClick={fetchAll} className="bg-slate-900 text-white" data-testid="refresh-loyalty-btn">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Enroll Modal */}
      {showEnroll && (
        <div data-testid="enroll-modal" className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-['Outfit'] font-semibold text-slate-900">Enroll in Loyalty Program</h3>
            <Button variant="ghost" size="sm" onClick={() => setShowEnroll(false)}><X className="w-4 h-4" /></Button>
          </div>
          <Select value={enrollClient} onValueChange={setEnrollClient}>
            <SelectTrigger className="border-slate-200" data-testid="enroll-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
            <SelectContent>{unenrolled.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.first_name} {c.last_name}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={enroll} className="bg-slate-900 text-white" data-testid="enroll-submit-btn">Enroll</Button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div data-testid="stat-members" className="bg-white border border-slate-200 rounded-2xl p-5">
          <Users className="w-5 h-5 text-[#8B5CF6] mb-2" />
          <p className="font-['Outfit'] text-2xl font-semibold text-slate-900">{dashboard?.total_members || 0}</p>
          <p className="text-xs text-slate-400">Members</p>
        </div>
        <div data-testid="stat-points" className="bg-white border border-slate-200 rounded-2xl p-5">
          <Coins className="w-5 h-5 text-[#F59E0B] mb-2" />
          <p className="font-['Outfit'] text-2xl font-semibold text-slate-900">{(dashboard?.total_points_outstanding || 0).toLocaleString()}</p>
          <p className="text-xs text-slate-400">Points Outstanding</p>
        </div>
        <div data-testid="stat-redeemed" className="bg-white border border-slate-200 rounded-2xl p-5">
          <Gift className="w-5 h-5 text-emerald-600 mb-2" />
          <p className="font-['Outfit'] text-2xl font-semibold text-slate-900">{(dashboard?.total_points_redeemed || 0).toLocaleString()}</p>
          <p className="text-xs text-slate-400">Points Redeemed</p>
        </div>
        <div data-testid="stat-rate" className="bg-white border border-slate-200 rounded-2xl p-5">
          <Star className="w-5 h-5 text-[#EC4899] mb-2" />
          <p className="font-['Outfit'] text-2xl font-semibold text-slate-900">{config.points_per_dollar || 10}x</p>
          <p className="text-xs text-slate-400">Pts per Dollar</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        {[{ key: "overview", label: "Overview" }, { key: "members", label: "Members" }, { key: "rewards", label: "Rewards" }, { key: "rules", label: "Rules" }].map(t => (
          <button key={t.key} onClick={() => t.key === "rules" ? openRules() : setTab(t.key)} data-testid={`tab-${t.key}`}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === t.key ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-rose-300"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {tab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Tier Distribution */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <h3 className="font-['Outfit'] font-semibold text-slate-900 mb-4">Tier Distribution</h3>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={dashboard?.tier_distribution || []} dataKey="count" nameKey="tier" cx="50%" cy="50%" outerRadius={80} innerRadius={40} paddingAngle={3}>
                    {(dashboard?.tier_distribution || []).map((entry, i) => (
                      <Cell key={i} fill={entry.color || "#ccc"} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v, name) => [v, name]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-3 mt-2 justify-center">
              {(dashboard?.tier_distribution || []).map(t => (
                <div key={t.tier} className="flex items-center gap-1.5 text-xs">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: t.color }} />
                  <span className="text-slate-600">{t.tier}: <strong>{t.count}</strong></span>
                </div>
              ))}
            </div>
          </div>

          {/* Tier Ladder */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <h3 className="font-['Outfit'] font-semibold text-slate-900 mb-4">Tier Benefits</h3>
            <div className="space-y-3">
              {tiers.map((t, i) => (
                <div key={i} data-testid={`tier-info-${t.name}`} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: t.color }}>
                    <Crown className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-slate-900">{t.name}</p>
                    <p className="text-xs text-slate-400">{t.min_points.toLocaleString()}+ points</p>
                  </div>
                  <span className="text-sm font-semibold text-slate-900">{t.discount_pct}% off</span>
                </div>
              ))}
            </div>
          </div>

          {/* Top Members */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5 lg:col-span-2">
            <h3 className="font-['Outfit'] font-semibold text-slate-900 mb-4">Top Loyalty Members</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {(dashboard?.top_members || []).slice(0, 5).map((m, i) => (
                <div key={m.client_id} className="border border-slate-200 rounded-lg p-3 text-center cursor-pointer hover:shadow-sm transition-shadow" onClick={() => viewMember(m.client_id)}>
                  <div className="w-8 h-8 rounded-full mx-auto mb-1 flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: tierColors[m.tier] || "#ccc" }}>
                    {i + 1}
                  </div>
                  <p className="text-sm font-medium text-slate-900 truncate">{m.client_name}</p>
                  <p className="text-xs text-slate-400">{m.total_earned?.toLocaleString()} pts</p>
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-medium mt-1 inline-block" style={{ backgroundColor: `${tierColors[m.tier]}20`, color: tierColors[m.tier] }}>{m.tier}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Members Tab */}
      {tab === "members" && (
        <div>
          <div className="flex gap-2 mb-4 flex-wrap">
            {["all", ...tiers.map(t => t.name)].map(t => (
              <button key={t} onClick={() => setTierFilter(t)} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${tierFilter === t ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
                {t === "all" ? `All (${members.length})` : `${t} (${members.filter(m => m.tier === t).length})`}
              </button>
            ))}
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-[#FAFAF9]">
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase">Member</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-slate-400 uppercase">Tier</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-slate-400 uppercase">Balance</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-slate-400 uppercase">Earned</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-slate-400 uppercase">Redeemed</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-slate-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredMembers.map(m => (
                  <tr key={m.client_id} data-testid={`member-row-${m.client_id}`} className="border-b border-slate-100 hover:bg-[#FAFAF9]">
                    <td className="py-3 px-4">
                      <p className="text-sm font-medium text-slate-900">{m.client_name}</p>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="text-xs px-2.5 py-1 rounded-full font-semibold" style={{ backgroundColor: `${tierColors[m.tier]}20`, color: tierColors[m.tier] }}>{m.tier}</span>
                    </td>
                    <td className="py-3 px-4 text-center font-semibold text-slate-900">{m.points_balance?.toLocaleString()}</td>
                    <td className="py-3 px-4 text-center text-sm text-emerald-600">{m.total_earned?.toLocaleString()}</td>
                    <td className="py-3 px-4 text-center text-sm text-slate-400">{m.total_redeemed?.toLocaleString()}</td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex justify-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => viewMember(m.client_id)} className="text-slate-600"><ChevronRight className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => setShowRedeem(m)} className="text-[#8B5CF6]" data-testid={`redeem-btn-${m.client_id}`}><Gift className="w-4 h-4" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Rewards Tab */}
      {tab === "rewards" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rewards.map((r, i) => (
            <div key={r.reward_id} data-testid={`reward-${r.reward_id}`} className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-sm transition-shadow">
              <Gift className="w-6 h-6 text-[#8B5CF6] mb-3" />
              <h3 className="font-['Outfit'] font-semibold text-slate-900">{r.name}</h3>
              <div className="flex items-center gap-2 mt-2">
                <Coins className="w-4 h-4 text-[#F59E0B]" />
                <span className="text-sm font-semibold text-[#F59E0B]">{r.points_cost?.toLocaleString()} points</span>
              </div>
              <p className="text-xs text-slate-400 mt-1 capitalize">{r.type}</p>
            </div>
          ))}
        </div>
      )}

      {/* Rules Tab */}
      {tab === "rules" && rulesForm && (
        <div className="space-y-6">
          {/* Point Valuation */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
            <h3 className="font-['Outfit'] font-semibold text-slate-900 flex items-center gap-2"><DollarSign className="w-5 h-5 text-emerald-600" /> Point Valuation</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Points earned per $1 spent</label>
                <Input type="number" min={1} value={rulesForm.points_per_dollar} onChange={e => setRulesForm(f => ({ ...f, points_per_dollar: +e.target.value }))} className="border-slate-200" data-testid="rule-pts-per-dollar" />
                <p className="text-[10px] text-slate-400 mt-0.5">e.g. 10 = client earns 10 pts per $1</p>
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Dollar value per point</label>
                <Input type="number" step={0.001} min={0} value={rulesForm.points_dollar_value} onChange={e => setRulesForm(f => ({ ...f, points_dollar_value: +e.target.value }))} className="border-slate-200" data-testid="rule-dollar-value" />
                <p className="text-[10px] text-slate-400 mt-0.5">e.g. 0.01 = 100 pts = $1.00</p>
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Min points to redeem</label>
                <Input type="number" min={0} value={rulesForm.min_redeem_points} onChange={e => setRulesForm(f => ({ ...f, min_redeem_points: +e.target.value }))} className="border-slate-200" data-testid="rule-min-redeem" />
              </div>
            </div>
          </div>

          {/* Bonus Points */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
            <h3 className="font-['Outfit'] font-semibold text-slate-900 flex items-center gap-2"><Gift className="w-5 h-5 text-[#8B5CF6]" /> Bonus Points</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Referral Bonus</label>
                <Input type="number" min={0} value={rulesForm.referral_bonus} onChange={e => setRulesForm(f => ({ ...f, referral_bonus: +e.target.value }))} className="border-slate-200" data-testid="rule-referral-bonus" />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Review Bonus</label>
                <Input type="number" min={0} value={rulesForm.review_bonus} onChange={e => setRulesForm(f => ({ ...f, review_bonus: +e.target.value }))} className="border-slate-200" data-testid="rule-review-bonus" />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Birthday Bonus</label>
                <Input type="number" min={0} value={rulesForm.birthday_bonus} onChange={e => setRulesForm(f => ({ ...f, birthday_bonus: +e.target.value }))} className="border-slate-200" data-testid="rule-birthday-bonus" />
              </div>
            </div>
          </div>

          {/* Expiration */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
            <h3 className="font-['Outfit'] font-semibold text-slate-900 flex items-center gap-2"><Timer className="w-5 h-5 text-[#F59E0B]" /> Point Expiration</h3>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setRulesForm(f => ({ ...f, points_expire: !f.points_expire }))}
                data-testid="rule-expire-toggle"
                className={`relative w-11 h-6 rounded-full transition-colors ${rulesForm.points_expire ? "bg-slate-900" : "bg-[#E6E4E0]"}`}
              >
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${rulesForm.points_expire ? "left-[22px]" : "left-0.5"}`} />
              </button>
              <div>
                <p className="text-sm font-medium text-slate-900">Points expire after inactivity</p>
                <p className="text-xs text-slate-400">{rulesForm.points_expire ? "Points will expire" : "Points never expire"}</p>
              </div>
            </div>
            {rulesForm.points_expire && (
              <div className="max-w-xs">
                <label className="text-xs text-slate-400 mb-1 block">Days until expiration</label>
                <Input type="number" min={30} value={rulesForm.expiry_days} onChange={e => setRulesForm(f => ({ ...f, expiry_days: +e.target.value }))} className="border-slate-200" data-testid="rule-expiry-days" />
                <p className="text-[10px] text-slate-400 mt-0.5">Points expire {rulesForm.expiry_days} days after being earned if unused</p>
              </div>
            )}
          </div>

          {/* Tiers */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-['Outfit'] font-semibold text-slate-900 flex items-center gap-2"><Crown className="w-5 h-5 text-[#F59E0B]" /> Loyalty Tiers ({editTiers.length})</h3>
              <Button size="sm" variant="outline" onClick={addTier} className="border-slate-200 text-slate-600" data-testid="add-tier-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Tier
              </Button>
            </div>
            <div className="space-y-3">
              {editTiers.map((tier, i) => (
                <div key={tier._key} data-testid={`edit-tier-${i}`} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-[#FAFAF9]">
                  <input type="color" value={tier.color || "#6B7280"} onChange={e => updateTier(i, "color", e.target.value)} className="w-8 h-8 rounded-lg border-0 cursor-pointer p-0" />
                  <div className="flex-1 grid grid-cols-3 gap-2">
                    <Input value={tier.name} onChange={e => updateTier(i, "name", e.target.value)} placeholder="Tier name" className="border-slate-200 h-8 text-sm" />
                    <div className="relative">
                      <Input type="number" min={0} value={tier.min_points} onChange={e => updateTier(i, "min_points", +e.target.value)} className="border-slate-200 h-8 text-sm pr-8" />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">pts</span>
                    </div>
                    <div className="relative">
                      <Input type="number" min={0} max={100} value={tier.discount_pct} onChange={e => updateTier(i, "discount_pct", +e.target.value)} className="border-slate-200 h-8 text-sm pr-6" />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">%</span>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => removeTier(i)} className="text-rose-600 p-1 h-auto" data-testid={`remove-tier-${i}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          {/* Rewards / Perks */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-['Outfit'] font-semibold text-slate-900 flex items-center gap-2"><Gift className="w-5 h-5 text-[#EC4899]" /> Redeemable Rewards ({editRewards.length})</h3>
              <Button size="sm" variant="outline" onClick={addReward} className="border-slate-200 text-slate-600" data-testid="add-reward-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Reward
              </Button>
            </div>
            <div className="space-y-3">
              {editRewards.map((rwd, i) => (
                <div key={rwd._key} data-testid={`edit-reward-${i}`} className="p-4 rounded-lg border border-slate-200 bg-[#FAFAF9] space-y-2">
                  <div className="flex items-center gap-2">
                    <Input value={rwd.name} onChange={e => updateReward(i, "name", e.target.value)} placeholder="Reward name" className="border-slate-200 h-8 text-sm flex-1" />
                    <select value={rwd.type} onChange={e => updateReward(i, "type", e.target.value)} className="h-8 text-xs border border-slate-200 rounded-lg px-2 bg-white text-slate-600">
                      <option value="discount">Discount ($)</option>
                      <option value="service">Free Service</option>
                      <option value="product">Free Product</option>
                      <option value="upgrade">Service Upgrade</option>
                    </select>
                    <Button variant="ghost" size="sm" onClick={() => removeReward(i)} className="text-rose-600 p-1 h-auto" data-testid={`remove-reward-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="relative">
                      <Input type="number" min={0} value={rwd.points_cost || 0} onChange={e => updateReward(i, "points_cost", +e.target.value)} className="border-slate-200 h-8 text-sm pr-8" />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">pts</span>
                    </div>
                    {rwd.type === "discount" && (
                      <div className="relative">
                        <Input type="number" min={0} value={rwd.value || 0} onChange={e => updateReward(i, "value", +e.target.value)} className="border-slate-200 h-8 text-sm pl-5" />
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">$</span>
                      </div>
                    )}
                    <Input value={rwd.description || ""} onChange={e => updateReward(i, "description", e.target.value)} placeholder="Description" className="border-slate-200 h-8 text-xs col-span-1" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Save Button */}
          <div className="flex gap-3">
            <Button onClick={saveRules} disabled={savingRules} className="bg-slate-900 text-white px-8" data-testid="save-rules-btn">
              <Save className="w-4 h-4 mr-1.5" /> {savingRules ? "Saving..." : "Save All Rules"}
            </Button>
            <Button variant="outline" onClick={() => setTab("overview")} className="border-slate-200">Cancel</Button>
          </div>
        </div>
      )}

      {/* Redeem Modal */}
      {showRedeem && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" onClick={() => setShowRedeem(null)}>
          <div data-testid="redeem-modal" className="bg-white rounded-2xl p-6 max-w-md w-full space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-['Outfit'] font-semibold text-slate-900">Redeem for {showRedeem.client_name}</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowRedeem(null)}><X className="w-4 h-4" /></Button>
            </div>
            <p className="text-sm text-slate-400">Balance: <strong>{showRedeem.points_balance?.toLocaleString()} points</strong></p>
            <div className="space-y-2">
              {rewards.map(r => (
                <button key={r.reward_id} disabled={showRedeem.points_balance < r.points_cost}
                  onClick={() => redeemReward(showRedeem.client_id, r.reward_id)}
                  className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all ${showRedeem.points_balance >= r.points_cost ? "border-slate-200 hover:border-[#8B5CF6] hover:bg-[#FAFAFA] cursor-pointer" : "border-slate-100 bg-[#FAFAF9] opacity-50 cursor-not-allowed"}`}>
                  <div className="text-left">
                    <p className="text-sm font-medium text-slate-900">{r.name}</p>
                    <p className="text-xs text-slate-400">{r.points_cost?.toLocaleString()} points</p>
                  </div>
                  <Gift className="w-4 h-4 text-[#8B5CF6]" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Member Detail Slide */}
      {selectedMember && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-end z-50" onClick={() => setSelectedMember(null)}>
          <div data-testid="member-detail" className="bg-white h-full w-full max-w-md p-6 overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-['Outfit'] text-lg font-semibold text-slate-900">{selectedMember.client_name}</h3>
              <Button variant="ghost" size="sm" onClick={() => setSelectedMember(null)}><X className="w-4 h-4" /></Button>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-6">
              <div className="bg-[#FAFAFA] rounded-lg p-3 text-center">
                <p className="text-xs text-slate-400">Tier</p>
                <p className="font-semibold" style={{ color: tierColors[selectedMember.tier] }}>{selectedMember.tier}</p>
              </div>
              <div className="bg-[#FAFAFA] rounded-lg p-3 text-center">
                <p className="text-xs text-slate-400">Balance</p>
                <p className="font-semibold text-slate-900">{selectedMember.points_balance?.toLocaleString()}</p>
              </div>
              <div className="bg-[#FAFAFA] rounded-lg p-3 text-center">
                <p className="text-xs text-slate-400">Total Earned</p>
                <p className="font-semibold text-emerald-600">{selectedMember.total_earned?.toLocaleString()}</p>
              </div>
              <div className="bg-[#FAFAFA] rounded-lg p-3 text-center">
                <p className="text-xs text-slate-400">Redeemed</p>
                <p className="font-semibold text-slate-400">{selectedMember.total_redeemed?.toLocaleString()}</p>
              </div>
            </div>
            {/* Next tier */}
            {tiers.length > 0 && (() => {
              const nextTier = tiers.find(t => t.min_points > (selectedMember.total_earned || 0));
              if (!nextTier) return <p className="text-xs text-emerald-600 bg-[#D4EDDA] rounded-lg p-3 mb-4 text-center">Max tier reached!</p>;
              const gap = nextTier.min_points - (selectedMember.total_earned || 0);
              return <p className="text-xs text-[#856404] bg-[#FEF3CD] rounded-lg p-3 mb-4 text-center">{gap.toLocaleString()} points to {nextTier.name} ({nextTier.discount_pct}% discount)</p>;
            })()}
            <h4 className="font-['Outfit'] font-semibold text-slate-900 mb-3 text-sm uppercase tracking-wider">History</h4>
            <div className="space-y-2">
              {(selectedMember.history || []).map((h, i) => (
                <div key={i} className="flex items-center gap-3 p-2 rounded border border-slate-100">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${h.type === "earn" ? "bg-[#D4EDDA] text-[#155724]" : "bg-[#FDE8E8] text-rose-600"}`}>
                    {h.type === "earn" ? "+" : "-"}
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-medium text-slate-900">{h.reason}</p>
                    <p className="text-[10px] text-slate-400">{new Date(h.created_at).toLocaleDateString()}</p>
                  </div>
                  <span className={`text-sm font-semibold ${h.type === "earn" ? "text-emerald-600" : "text-rose-600"}`}>
                    {h.points > 0 ? "+" : ""}{h.points?.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
